/**
 * `HistogramChart` — design §4 step 9's "distribution plot" over `conditions`' `by_unit[]`
 * shares. Hand-built SVG in `BaselineCharts.tsx`'s register (the repo forbids chart
 * libraries, plan Global Constraints): viewBox-scaled, one horizontal bar per row, a value
 * label beside each bar rather than an axis a reader has to read off.
 */
import { fmtNum } from '../../../lib/format';

export const HG_W = 320;
export const HG_ROW_H = 22;
export const HG_ROW_GAP = 10;
export const HG_LABEL_W = 110;
export const HG_VALUE_W = 52;
export const HG_PAD_TOP = 8;
export const HG_MIN_H = 60;

const plotX = HG_LABEL_W;
const plotW = HG_W - HG_LABEL_W - HG_VALUE_W;
const pitch = HG_ROW_H + HG_ROW_GAP;

export interface HistogramRow {
  id: string;
  label: string;
  /** A share, 0–100; `null` draws no bar and prints an em dash. */
  value: number | null;
}

export interface HistogramChartProps {
  rows: HistogramRow[];
  title: string;
  color?: string;
  selectedId?: string | null;
  onSelect?: (id: string) => void;
}

/** The chart's own `aria-label` — a plain-text readout of every row, for §I5 and the test list. */
function readout(title: string, rows: HistogramRow[]): string {
  const parts = rows.map((r) => `${r.label}: ${r.value == null ? '—' : `${fmtNum(r.value, 1)}%`}`);
  return `${title} — ${parts.join(', ')}`;
}

export function HistogramChart({ rows, title, color = 'var(--brand)', selectedId, onSelect }: HistogramChartProps) {
  const h = Math.max(HG_MIN_H, HG_PAD_TOP + Math.max(rows.length, 1) * pitch);
  return (
    <svg
      className="intel-chart hg-chart"
      viewBox={`0 0 ${HG_W} ${h}`}
      role="img"
      aria-label={readout(title, rows)}
      data-testid="histogram-chart"
    >
      {rows.map((row, i) => {
        const y = HG_PAD_TOP + i * pitch;
        const w = row.value == null ? 0 : Math.max(2, (Math.min(row.value, 100) / 100) * plotW);
        const text = row.value == null ? '—' : `${fmtNum(row.value, 1)}%`;
        const selected = row.id === selectedId;
        return (
          <g
            key={row.id}
            className="hg-row"
            data-unit={row.id}
            style={onSelect ? { cursor: 'pointer' } : undefined}
            onClick={onSelect ? () => onSelect(row.id) : undefined}
          >
            <title>{`${row.label} · ${text}`}</title>
            <text className="hg-rowlabel" x={plotX - 10} y={y + HG_ROW_H / 2 + 4} textAnchor="end">
              {row.label}
            </text>
            {w > 0 ? (
              <rect
                x={plotX}
                y={y}
                width={w}
                height={HG_ROW_H}
                rx={3}
                fill={color}
                opacity={selected ? 1 : 0.78}
                stroke={selected ? 'var(--ink)' : 'none'}
                strokeWidth={selected ? 1.5 : 0}
              />
            ) : null}
            <text className="hg-num" x={plotX + w + 6} y={y + HG_ROW_H / 2 + 4}>
              {text}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export default HistogramChart;
